import { useEffect, useState, useCallback } from 'react';

import { getPowerSubscriptionPlans, getPowerSubscriptionDetails } from '../api/checkoutApi';
import { getApiAxiosInstance } from '../utils/network';
import { stripePlans, SubscriptionIntervalTypes } from '../utils/constants';

const API_STATUS = {
    SUCCESS: 'success',
};

/**
 * Hook that manages Power subscription plan fetching, pricing, coupons, and BIN mismatch.
 * Extracted from UpgradeToPowerPopupV4 for reuse in SidebarPowerCheckout.
 *
 * @param {object} options
 * @param {string} [options.defaultInterval] - Default billing interval (defaults to 'year')
 * @returns {object} Plan state and helpers
 */
const usePowerSubscriptionPlans = ({ defaultInterval = SubscriptionIntervalTypes.YEAR, enabled = true } = {}) => {
    const [interval, setInterval] = useState(defaultInterval);
    const [subscriptionPlans, setSubscriptionPlans] = useState([]);
    const [couponCode, setCouponCode] = useState(null);
    const [couponError, setCouponError] = useState('');
    const [invoiceItems, setInvoiceItems] = useState(null);
    const [isLoadingPricing, setIsLoadingPricing] = useState(true);
    const [binCountryError, setBinCountryError] = useState('');
    const [isTennisAustraliaMember, setIsTennisAustraliaMember] = useState(false);

    const isV2022Plan = (targetInterval = null, plansToUse = null) => {
        const intervalToUse = targetInterval || interval;
        const plansArray = plansToUse || subscriptionPlans;
        const selectedPlan = plansArray.find(plan => plan.interval === intervalToUse);
        const planId = selectedPlan?.planId;
        return planId === stripePlans.members.powerAnnual || planId === stripePlans.members.powerMonthly;
    };

    const fetchSubscriptionDetails = async (targetInterval = null, couponCodeParam = null, plansToUse = null) => {
        try {
            const intervalToUse = targetInterval || interval;
            const plansArray = plansToUse || subscriptionPlans;
            const selectedPlan = plansArray.find(plan => plan.interval === intervalToUse);
            const planId = selectedPlan?.planId;

            if (!planId) {
                if (couponCodeParam) {
                    setCouponError('Plan not available');
                }
                return null;
            }

            const apiParams = { plan: planId };
            if (couponCodeParam) {
                apiParams.couponCode = couponCodeParam;
            }

            const response = await getPowerSubscriptionDetails(getApiAxiosInstance(), apiParams);
            if (response.data.status === API_STATUS.SUCCESS) {
                if (couponCodeParam) {
                    setCouponError('');
                }
                return response.data.data;
            }

            if (couponCodeParam) {
                setCouponError(response.data.message || 'Invalid coupon code');
            }
            return null;
        } catch (_error) { // NOSONAR — error is intentionally swallowed; user sees coupon error state
            if (couponCodeParam) {
                setCouponError('Error applying coupon');
            }
            return null;
        }
    };

    const fetchSubscriptionPlans = async () => {
        try {
            const response = await getPowerSubscriptionPlans(getApiAxiosInstance());
            if (response?.data?.subscriptionPlans) {
                setSubscriptionPlans(response.data.subscriptionPlans);
            }
            if (response?.data?.isTennisAustraliaMember) {
                setIsTennisAustraliaMember(true);
            }
        } catch (_error) { // NOSONAR — error is intentionally swallowed; plans reset to empty
            setSubscriptionPlans([]);
        }
    };

    // Fetch plans on mount (skip if disabled)
    useEffect(() => {
        if (enabled) {
            fetchSubscriptionPlans();
        }
    }, [enabled]);

    // Load subscription details when plans or interval change
    useEffect(() => {
        const loadSubscriptionDetails = async () => {
            if (subscriptionPlans.length > 0) {
                setIsLoadingPricing(true);
                if (isV2022Plan()) {
                    const invoiceData = await fetchSubscriptionDetails();
                    setInvoiceItems(invoiceData);
                } else {
                    setInvoiceItems(null);
                }
                setIsLoadingPricing(false);
            }
        };
        loadSubscriptionDetails();
    }, [subscriptionPlans, interval]);

    const handleBinCountryMismatch = useCallback(async (availablePowerPlans, errorMessage) => {
        setIsLoadingPricing(true);
        if (availablePowerPlans.subscriptionPlans && availablePowerPlans.subscriptionPlans.length > 0) {
            setSubscriptionPlans(availablePowerPlans.subscriptionPlans);
        }
        if (availablePowerPlans.isTennisAustraliaMember !== undefined) {
            setIsTennisAustraliaMember(availablePowerPlans.isTennisAustraliaMember);
        }
        setBinCountryError(errorMessage);
        setCouponCode(null);
        setCouponError('');

        const newPlans = availablePowerPlans.subscriptionPlans || subscriptionPlans;
        if (isV2022Plan(interval, newPlans)) {
            const invoiceData = await fetchSubscriptionDetails(interval, null, newPlans);
            setInvoiceItems(invoiceData);
        } else {
            setInvoiceItems(null);
        }
        setIsLoadingPricing(false);
    }, [interval, subscriptionPlans]);

    const applyCoupon = useCallback(async (couponCodeParam) => {
        if (!couponCodeParam || couponCodeParam.trim() === '') {
            setCouponError('Please enter a coupon code');
            return;
        }

        if (isV2022Plan()) {
            const invoiceData = await fetchSubscriptionDetails(null, couponCodeParam.trim());
            setInvoiceItems(invoiceData);
            if (invoiceData) {
                setCouponCode(couponCodeParam.trim());
            }
        } else {
            setCouponError('Coupon codes are not supported for this plan');
        }
    }, [interval, subscriptionPlans]);

    const getYearlyPlan = () => subscriptionPlans.find(plan => plan.interval === SubscriptionIntervalTypes.YEAR);

    const getMonthlyPlan = () => subscriptionPlans.find(plan => plan.interval === SubscriptionIntervalTypes.MONTH);

    const getSelectedPlan = () => subscriptionPlans.find(plan => plan.interval === interval);

    const getSavingsPercentage = () => {
        const yearlyPlan = getYearlyPlan();
        return yearlyPlan ? yearlyPlan.savingsPercentage : 0;
    };

    return {
        interval,
        setInterval,
        subscriptionPlans,
        couponCode,
        couponError,
        invoiceItems,
        isLoadingPricing,
        binCountryError,
        isTennisAustraliaMember,
        handleBinCountryMismatch,
        applyCoupon,
        getYearlyPlan,
        getMonthlyPlan,
        getSelectedPlan,
        getSavingsPercentage,
    };
};

export default usePowerSubscriptionPlans;
